import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { db } from '../lib/db'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { sortPlayersByScore, effectivePar, effectiveHandicapRank, totalParForGender, strokesOnHole, resolveCourseHandicap } from '../lib/golf'
import { useDisplayMode } from '../lib/displayMode'
import { isScorecardPrivacyGated } from '../lib/scorecardPrivacy'
import DisplayModeToggle from '../components/DisplayModeToggle'
import SkinsCard from '../components/SkinsCard'
import WolfCard from '../components/WolfCard'
import VegasCard from '../components/VegasCard'

// First name + last initial — "Jacob Drumm" → "Jacob D."
// Team-aggregated rows have player_name like "Team 1 — Jacob, Player 2".
// The naive split-then-last-initial logic ate the digit at the end of the
// last member's name and slapped it after "Team", producing labels like
// "Team 2." (from Team 1 ending in "Player 2") or "Team 4." (from Team 2
// ending in "Player 4") — totally bogus team numbers. Pass team rows
// through verbatim (the leading "Team N" already says what we need).
function fmtName(playerName) {
  if (!playerName) return '–'
  if (playerName.startsWith('Team ')) {
    // Trim to just "Team N" — drop the " — members" suffix so the row
    // stays compact and the team number isn't competing for space with
    // member names (which are still listed in the Individual Scores
    // section below).
    const dash = playerName.indexOf(' — ')
    return dash > -1 ? playerName.slice(0, dash) : playerName
  }
  const parts = playerName.trim().split(' ')
  if (parts.length === 1) return parts[0]
  return `${parts[0]} ${parts[parts.length - 1][0]}.`
}

export default function RoundLeaderboardPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { standalone_round_id, course, holes: holeCount, starting_hole: startingHoleFromState } = location.state || {}
  const [displayMode] = useDisplayMode()

  const [standings, setStandings] = useState([])
  const [holeData, setHoleData] = useState([])
  const [loading, setLoading] = useState(true)
  // F-104-T5: DB-sourced round length. location.state carries at most a hole
  // COUNT (via `holes`) — never a starting_hole — and a shared leaderboard
  // link arrives with no state at all. Fetched sequentially inside fetchData
  // via a thin get_round_meta read; cached in a ref so the 6s poll skips the
  // extra RTT. Mirrored into state purely so the render-time allHoles
  // fallback below picks it up without a reload.
  const roundMetaRef = useRef(null)
  const [roundMeta, setRoundMeta] = useState(null)
  // Raw per-player rows (pre-team-collapse) preserved for the per-player
  // detail scorecard rendered below the team leaderboard on team rounds.
  // Lets players verify/dispute the underlying individual scores when the
  // top view collapses to team totals.
  const [individualScores, setIndividualScores] = useState([])
  // Side games played during this round — Skins / Wolf / etc. Fetched
  // via the standalone-game function's list_games action. Shown as a
  // read-only recap section below the leaderboard so the final results
  // stay on-screen after the round is saved (a live scorecard closes
  // when hole 18 is written; the recap is where players see who won).
  const [sideGames, setSideGames] = useState([])
  // 'stroke' | 'scramble' | '2v2' | 'match' | null — drives whether to
  // render the match-play status banner and the per-player detail scorecard.
  const [roundType, setRoundType] = useState(null)
  const { player: authPlayer } = useAuth()
  // Fall back to the legacy localStorage 'golf_player' key for older sessions
  // where AuthContext hasn't populated yet on first render.
  const currentPlayer = authPlayer || (() => {
    try { return JSON.parse(localStorage.getItem('golf_player')) } catch { return null }
  })()

  // The signed-in player's row (if they scored in this round) — powers the
  // "Edit my scores" button on the detail page.
  const myScoreRow = currentPlayer?.id
    ? individualScores.find(sc => sc.player_id === currentPlayer.id)
    : null
  const canEditThisRound = !!(myScoreRow && !myScoreRow.attested && !myScoreRow.event_id)

  const par = course?.par || holeData.reduce((s, h) => s + (h.par || 4), 0) || 72

  // Convert a round_scores row's raw handicap_before (a WHS Index) to a
  // Course Handicap for this round using the tee's snapshotted rating/
  // slope + the course par. Falls back to raw Index when any input is
  // missing (older rounds pre-snapshot columns / manual courses).
  const courseHcpFor = (sc) => resolveCourseHandicap(
    sc?.handicap_before,
    sc?.slope_rating_snapshot,
    sc?.course_rating_snapshot,
    par,
  )

  const fetchData = useCallback(async () => {
    if (!standalone_round_id) { setLoading(false); return }
    try {
      // F-104-T5: resolve the round's declared length BEFORE building the
      // scorecard grid. location.state can carry at most a hole COUNT (via
      // `holes`) — never a starting_hole — and a shared leaderboard link
      // arrives with no state at all. A back-9 round whose state said
      // `holes: 9` previously rendered rows 1-9 instead of 10-18; the DB
      // read closes that gap. Sequential await (no Promise.all — single
      // max:1 pooler client, see CLAUDE.md). Cached in roundMetaRef so the
      // 6s poll skips the extra round-trip after the first run. Pre-migration
      // deploy window: get_round_meta returns {hole_count:null,
      // starting_hole:null} and the location.state fallback kicks in.
      if (!roundMetaRef.current) {
        try {
          const { data: m } = await db('get_round_meta', { standalone_round_id })
          if (m && m.hole_count != null) {
            const resolved = {
              hole_count: Number(m.hole_count),
              starting_hole: Number(m.starting_hole) || 1,
            }
            roundMetaRef.current = resolved
            setRoundMeta(resolved)
          }
        } catch { /* fall through to location.state */ }
      }
      // Outbound friend-privacy gate (F-039): a non-participant viewer only
      // sees a friend's per-hole scorecard / detailed stats when the friend
      // opted in; a participant / scorekeeper of the round keeps full detail.
      // The server keys the gate on the JWT-VERIFIED caller (db() attaches the
      // token for get_standalone_scores), so viewer identity is not
      // spoofable — the viewer_id below is vestigial and ignored server-side.
      const { data: scores } = await db('get_standalone_scores', {
        standalone_round_id, viewer_id: currentPlayer?.id || null,
      })
      const holesFromCourse = course?.holes_data || []
      const stateCountN = parseInt(holeCount)
      const stateStartN = parseInt(startingHoleFromState)
      // Pre-migration deploy-window fallback (T5 build live, T1 migration not
      // yet applied): roundMetaRef.hole_count comes back null AND the T5
      // activity-card hand-off no longer carries a `holes` field, so without
      // another safety net the grid would default to count=18/start=1 and
      // render blank cells for a 9-hole or back-9 round. The hole_scores
      // JSON keys are the actual played holes — '1'..'9' for a front-9,
      // '10'..'18' for a back-9 — so derive {count, start} from them for
      // this window. Self-healing: once the migration lands, roundMeta
      // overrides this. Only consulted when roundMeta is missing.
      let scoreDerived = null
      if (!roundMetaRef.current && Array.isArray(scores) && scores.length > 0) {
        const keys = new Set()
        for (const sc of scores) {
          const hs = typeof sc.hole_scores === 'string'
            ? (() => { try { return JSON.parse(sc.hole_scores) } catch { return {} } })()
            : (sc.hole_scores || {})
          for (const k of Object.keys(hs)) {
            const n = Number(k)
            if (Number.isFinite(n) && n >= 1 && n <= 18) keys.add(n)
          }
        }
        if (keys.size > 0) {
          scoreDerived = { count: keys.size, start: Math.min(...keys) }
        }
      }
      const count = roundMetaRef.current?.hole_count
        || (Number.isFinite(stateCountN) ? stateCountN : null)
        || scoreDerived?.count
        || 18
      const startingHole = roundMetaRef.current?.starting_hole
        || (Number.isFinite(stateStartN) ? stateStartN : null)
        || scoreDerived?.start
        || 1
      // Gender-aware par/handicap_rank: female viewer (currentPlayer.gender)
      // sees women's par on her own scorecard if the course has women's data
      // populated. Other genders fall back to men's. Mixed-gender groups
      // currently use the viewer's gender for display — per-player gendered
      // par is post-launch.
      const viewerGender = currentPlayer?.gender || null
      const builtHoles = Array.from({ length: count }, (_, i) => {
        const holeNum = startingHole + i
        const match = holesFromCourse.find(h => h.hole_number === holeNum)
        return {
          hole_number: holeNum,
          par: effectivePar(match, viewerGender) || 4,
          handicap_rank: effectiveHandicapRank(match, viewerGender) || holeNum,
          par_men: match?.par ?? null,
          par_women: match?.par_women ?? null,
          handicap_rank_men: match?.handicap_rank ?? null,
          handicap_rank_women: match?.handicap_rank_women ?? null,
        }
      })
      setHoleData(builtHoles)

      const mapped = (scores || []).map(sc => {
        const hs = typeof sc.hole_scores === 'string' ? JSON.parse(sc.hole_scores) : (sc.hole_scores || {})
        return {
          ...sc,
          hole_scores: hs,
          holes_completed: sc.holes_completed || Object.keys(hs).length,
          total_score: sc.score_vs_par ?? sc.total_score ?? 0,
          gross_total: sc.total_score,
        }
      })
      // Stash the format + raw per-player rows for the detail scorecard /
      // status banner. round_type was added in 20260629140000; older rows
      // are NULL — fall back to is_scramble for those.
      const detectedType = mapped.find(sc => sc.round_type)?.round_type
        || (mapped.some(sc => sc.is_scramble) ? 'scramble' : null)
      setRoundType(detectedType)
      setIndividualScores(mapped)
      // Team-format collapse: any row carrying a team_number is part of a
      // team round and collapses one row per team_number.
      //   - Scramble: every member shares identical hole_scores → take the
      //     first one verbatim.
      //   - Best Ball: each member has their OWN hole_scores → team's hole
      //     score = min across members; team total = sum of per-hole bests.
      const hasTeams = mapped.some(sc => sc.team_number != null)
      // For a 2-player round the team wrapper is noise — "Team 1 · Jacob"
      // says nothing that "Jacob" doesn't. Drop the "Team N —" prefix and
      // let the row label be the member name(s) directly.
      const twoPlayerRound = mapped.length === 2
      let displayList = mapped
      if (hasTeams) {
        const isScrambleRound = mapped.some(sc => sc.is_scramble)
        const teams = new Map()
        for (const sc of mapped) {
          const t = sc.team_number ?? 1
          if (!teams.has(t)) {
            teams.set(t, { team_number: t, _members: [], _member_names: [] })
          }
          teams.get(t)._members.push(sc)
          if (sc.player_name) teams.get(t)._member_names.push(sc.player_name)
        }
        const teamLabel = (t) => {
          if (twoPlayerRound && t._member_names.length > 0) return t._member_names.join(', ')
          return `Team ${t.team_number}${t._member_names.length ? ` — ${t._member_names.join(', ')}` : ''}`
        }
        displayList = [...teams.values()].map(t => {
          if (isScrambleRound) {
            // All members share hole_scores — pluck the first one.
            const ref = t._members[0]
            return {
              ...ref,
              player_id: `team-${t.team_number}`,
              player_name: teamLabel(t),
            }
          }
          // Best Ball: compute the team's hole_scores as the per-hole min.
          const teamHoleScores = {}
          let teamTotal = 0
          let teamHolesCompleted = 0
          for (const h of builtHoles) {
            const k = String(h.hole_number)
            const memberScoresForHole = t._members
              .map(m => m.hole_scores[k])
              .map(v => v != null ? parseInt(v) : null)
              .filter(v => v != null)
            if (memberScoresForHole.length === 0) continue
            const best = Math.min(...memberScoresForHole)
            teamHoleScores[k] = best
            teamTotal += best
            teamHolesCompleted++
          }
          const teamVsPar = Object.entries(teamHoleScores).reduce((sum, [k, v]) => {
            const h = builtHoles.find(h => h.hole_number === parseInt(k))
            return sum + v - (h?.par || 4)
          }, 0)
          return {
            player_id: `team-${t.team_number}`,
            player_name: teamLabel(t),
            hole_scores: teamHoleScores,
            holes_completed: teamHolesCompleted,
            total_score: teamVsPar,     // matches the existing convention where total_score holds vs-par
            gross_total: teamTotal,
            // Preserve raw member rows so per-hole rendering can compute
            // net-best-ball at display time (each member's gross minus their
            // own handicap strokes, then MIN across members, then summed).
            _bestball_members: t._members,
            is_complete: t._members.every(m => m.is_complete),
            team_number: t.team_number,
          }
        })
      }
      const sorted = sortPlayersByScore(displayList, builtHoles)
      setStandings(sorted)
    } catch (err) { console.error('Round leaderboard error:', err) }
    setLoading(false)
  }, [standalone_round_id])

  useEffect(() => { fetchData() }, [fetchData])

  // Poll every 6s so live-round watchers see scores from other devices
  // as they come in without a manual refresh.
  useEffect(() => {
    if (!standalone_round_id) return
    const iv = setInterval(fetchData, 6000)
    return () => clearInterval(iv)
  }, [standalone_round_id, fetchData])

  // Load any side games created during this round for the recap section.
  // list_games requires the caller to be on the round; if the viewer
  // isn't, the endpoint 403s and we just render nothing (silent).
  useEffect(() => {
    if (!standalone_round_id) return
    let cancelled = false
    ;(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.access_token) return
        const res = await fetch('/.netlify/functions/standalone-game', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ action: 'list_games', standalone_round_id }),
        })
        if (!res.ok) return
        const json = await res.json()
        if (!cancelled) setSideGames(Array.isArray(json.data) ? json.data : [])
      } catch { /* silent — recap is best-effort */ }
    })()
    return () => { cancelled = true }
  }, [standalone_round_id])

  if (!standalone_round_id) {
    return (
      <div className="page"><div className="container" style={{ textAlign: 'center', paddingTop: 60 }}>
        <p className="text-muted">No round data.</p>
        <button className="btn btn-primary" onClick={() => navigate(-1)} style={{ marginTop: 16 }}>Back</button>
      </div></div>
    )
  }

  const is18 = holeData.length > 9
  // F-104-T5: render-time fallback used only when holeData hasn't landed yet
  // AND standings is non-empty (rare — see "No scores yet" branch above).
  // Mirrors the fetchData resolution: DB meta first, then location.state,
  // then the pre-F-104 default. Starting-hole-aware so a back-9 fallback
  // builds [10..18] rather than [1..9].
  const _stateCountN = parseInt(holeCount)
  const _stateStartN = parseInt(startingHoleFromState)
  const _fallbackCount = roundMeta?.hole_count || (Number.isFinite(_stateCountN) ? _stateCountN : 18)
  const _fallbackStart = roundMeta?.starting_hole || (Number.isFinite(_stateStartN) ? _stateStartN : 1)
  const allHoles = holeData.length > 0 ? holeData : Array.from({ length: _fallbackCount }, (_, i) => ({ hole_number: _fallbackStart + i, par: 4, handicap_rank: _fallbackStart + i }))

  // For match play: pre-compute running match state per hole from team 1's
  // perspective. matchStates[holeNumber] = { played, t1Lead, closedOut }
  // where t1Lead > 0 means Team 1 is up, < 0 means Team 2 is up, 0 = AS.
  // Bonus holes played after closeout freeze at the closeout state so the
  // official result isn't overwritten by post-match play.
  const matchStates = (() => {
    if (roundType !== 'match') return null
    const t1 = individualScores.filter(sc => sc.team_number === 1)
    const t2 = individualScores.filter(sc => sc.team_number === 2)
    if (t1.length === 0 || t2.length === 0) return null
    const sorted = [...allHoles].sort((a, b) => a.hole_number - b.hole_number)
    const total = sorted.length || 18
    const out = {}
    let t1Lead = 0
    let holesPlayed = 0
    let closedOut = false
    let closeoutSnapshot = null
    // In Adjusted mode, per-hole comparisons run on NET scores so the
    // weaker player pops on their handicap holes. Per-member strokes
    // derived from handicap_before on each round_scores row.
    const memberStrokesForHole = (sc, hole) => {
      if (displayMode !== 'adjusted') return 0
      const hcp = courseHcpFor(sc)
      if (!hcp) return 0
      return strokesOnHole(hcp, hole.handicap_rank, total)
    }
    for (const h of sorted) {
      const hn = String(h.hole_number)
      const t1Vals = t1
        .map(sc => ({ v: sc.hole_scores?.[hn], strokes: memberStrokesForHole(sc, h) }))
        .filter(x => x.v != null && x.v !== '' && Number.isFinite(parseInt(x.v)))
        .map(x => parseInt(x.v) - x.strokes)
      const t2Vals = t2
        .map(sc => ({ v: sc.hole_scores?.[hn], strokes: memberStrokesForHole(sc, h) }))
        .filter(x => x.v != null && x.v !== '' && Number.isFinite(parseInt(x.v)))
        .map(x => parseInt(x.v) - x.strokes)
      if (t1Vals.length === 0 || t2Vals.length === 0) {
        out[h.hole_number] = { played: false }
        continue
      }
      if (closedOut) {
        out[h.hole_number] = { played: true, t1Lead: closeoutSnapshot, closedOut: true }
        continue
      }
      holesPlayed++
      const t1Best = Math.min(...t1Vals)
      const t2Best = Math.min(...t2Vals)
      if (t1Best < t2Best) t1Lead++
      else if (t2Best < t1Best) t1Lead--
      const remaining = total - holesPlayed
      if (Math.abs(t1Lead) > remaining) {
        closedOut = true
        closeoutSnapshot = t1Lead
      }
      out[h.hole_number] = { played: true, t1Lead, closedOut }
    }
    return out
  })()
  const isMatchPlay = roundType === 'match' && matchStates != null

  // Adjusted mode is only meaningful if EVERY player in the round has a
  // round-start handicap on file — a single missing handicap would mix
  // gross + net cells and produce a bogus leaderboard. Hide the toggle
  // and lock display to Gross when that's the case.
  const handicapsComplete = individualScores.length > 0
    && individualScores.every(sc => sc.handicap_before != null)

  function holeClass(score, holePar) {
    if (!score || !holePar) return 'empty'
    const d = score - holePar
    if (d <= -2) return 'eagle'
    if (d === -1) return 'birdie'
    if (d === 0) return 'par'
    if (d === 1) return 'bogey'
    if (d === 2) return 'double'
    if (d === 3) return 'triple'
    return 'quad'
  }

  const holeStyle = {
    eagle: { background: 'var(--yellow-eagle)', color: 'var(--green-deep)', borderRadius: '50%' },
    birdie: { border: '1.5px solid var(--blue-birdie)', color: 'var(--blue-birdie)', borderRadius: '50%' },
    par: { color: 'var(--gray-300)' },
    bogey: { border: '1.5px solid var(--red)', borderRadius: 2, color: 'var(--cream)' },
    double: { background: 'var(--red)', borderRadius: 2, color: 'white' },
    triple: { background: '#7a1a1a', borderRadius: 2, color: 'white' },
    quad: { background: '#111111', borderRadius: 2, color: 'white' },
    empty: { color: 'var(--gray-700)', opacity: 0.5 },
  }

  const frontPar = allHoles.slice(0, 9).reduce((s, h) => s + (h.par || 4), 0)
  const backPar = is18 ? allHoles.slice(9, 18).reduce((s, h) => s + (h.par || 4), 0) : 0
  const totalPar = is18 ? frontPar + backPar : frontPar

  return (
    <div className="page">
      <div className="container">
        <div style={{ paddingTop: 12, paddingBottom: 8, marginBottom: 8, borderBottom: '1px solid var(--green-mid)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: 'var(--cream)', fontSize: '1.2rem', cursor: 'pointer', padding: '4px 0' }}>‹</button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ fontSize: '1.4rem', margin: 0, lineHeight: 1.2 }}>Scorecard</h1>
            {course?.name && <p className="text-muted text-sm" style={{ margin: 0, marginTop: 2, fontSize: '0.75rem' }}>{course.name}{par ? ` · Par ${par}` : ''}</p>}
          </div>
          {canEditThisRound && (
            <button
              type="button"
              onClick={() => navigate(`/edit-round/${myScoreRow.id}`)}
              title="Edit my scores"
              style={{ background: 'transparent', border: '1px solid var(--green-mid)', borderRadius: 6, padding: '6px 12px', color: 'var(--cream)', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', letterSpacing: '0.05em', flexShrink: 0 }}
            >
              EDIT
            </button>
          )}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <div className="spinner" style={{ margin: '0 auto 12px' }} />
            <p className="text-muted text-sm">Loading scores...</p>
          </div>
        ) : standings.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <p style={{ fontSize: '2rem', marginBottom: 8 }}>⛳</p>
            <p className="text-muted text-sm">No scores yet.</p>
          </div>
        ) : (
          <div>
            {/* Match-play status banner — only rendered when round_type is
                'match'. Computes hole-by-hole wins from the raw per-player
                rows (collapsed leaderboard above shows the same data as
                stroke totals; this banner shows the actual match result). */}
            {roundType === 'match' && (() => {
              const team1 = individualScores.filter(sc => sc.team_number === 1)
              const team2 = individualScores.filter(sc => sc.team_number === 2)
              if (team1.length === 0 || team2.length === 0) return null
              // 1v1 match — use player names in place of "Team 1"/"Team 2"
              // in the status banner so it reads "Jacob wins 4 & 3" instead
              // of "Team 1 wins 4 & 3".
              const isSolo = team1.length === 1 && team2.length === 1
              const sideLabel = (n) => isSolo
                ? fmtName((n === 1 ? team1 : team2)[0].player_name)
                : `Team ${n}`
              // Walk holes IN ORDER; freeze status when match closes out. A
              // 2&1 stays 2&1 even if the losing team takes the bonus 18th —
              // the win is recorded at the closeout hole, not the last
              // hole played. Read-only data path so no untouched-par
              // concern (all scores here are saved values).
              const sortedHoles = [...allHoles].sort((a, b) => a.hole_number - b.hole_number)
              const totalHoles = sortedHoles.length || 18
              let t1Wins = 0, t2Wins = 0, halved = 0, holesPlayed = 0
              let closeoutLeader = null, closeoutLead = 0, closeoutRemaining = 0
              for (const h of sortedHoles) {
                const k = String(h.hole_number)
                const t1 = team1.map(sc => sc.hole_scores[k]).filter(v => v != null).map(v => parseInt(v))
                const t2 = team2.map(sc => sc.hole_scores[k]).filter(v => v != null).map(v => parseInt(v))
                if (t1.length === 0 || t2.length === 0) continue
                const t1b = Math.min(...t1), t2b = Math.min(...t2)
                // Bonus holes after closeout don't change the official result.
                if (closeoutLeader != null) {
                  // (Could track bonus-hole results for a "post-match" display
                  // later; for now we just skip them.)
                  continue
                }
                holesPlayed++
                if (t1b < t2b) t1Wins++
                else if (t2b < t1b) t2Wins++
                else halved++
                const diff = t1Wins - t2Wins
                const absDiff = Math.abs(diff)
                const remaining = totalHoles - holesPlayed
                if (absDiff > remaining) {
                  closeoutLeader = diff > 0 ? 1 : 2
                  closeoutLead = absDiff
                  closeoutRemaining = remaining
                }
              }
              const diff = t1Wins - t2Wins
              const absDiff = Math.abs(diff)
              const holesRemaining = totalHoles - holesPlayed
              let status, color
              if (closeoutLeader != null) {
                status = `${sideLabel(closeoutLeader)} wins ${closeoutLead} & ${closeoutRemaining}`
                color = 'var(--green-bright)'
              } else if (holesPlayed === 0) { status = 'Match has not started'; color = 'var(--gray-300)' }
              else if (absDiff > 0 && absDiff === holesRemaining) {
                const leader = diff > 0 ? 1 : 2
                status = `${sideLabel(leader)} Dormie (${absDiff} UP, ${holesRemaining} to play)`
                color = 'var(--gold)'
              } else if (diff === 0) { status = `All Square (thru ${holesPlayed})`; color = 'var(--cream)' }
              else {
                const leader = diff > 0 ? 1 : 2
                status = `${sideLabel(leader)} ${absDiff} UP (thru ${holesPlayed})`
                color = 'var(--gold)'
              }
              return (
                <div style={{
                  margin: '0 12px 12px', padding: '10px 12px',
                  background: 'var(--green-deep)', border: '1px solid var(--gold)',
                  borderRadius: 'var(--radius)',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
                }}>
                  <div>
                    <div style={{ fontSize: '0.6rem', color: 'var(--gray-300)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Match Status</div>
                    <div style={{ fontSize: '0.95rem', fontWeight: 700, color }}>{status}</div>
                  </div>
                  <div style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--gray-300)' }}>
                    <div>
                      {isSolo
                        ? `${fmtName(team1[0].player_name)} ${t1Wins} · ${fmtName(team2[0].player_name)} ${t2Wins}`
                        : `T1 ${t1Wins} · T2 ${t2Wins}`}
                      {halved > 0 ? ` · ${halved} halved` : ''}
                    </div>
                  </div>
                </div>
              )
            })()}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 12px', borderBottom: '1px solid var(--green-mid)' }}>
              <p style={{ margin: 0, fontFamily: 'var(--font-mono)', fontSize: '0.65rem', color: 'var(--gray-500)' }}>
                ← swipe to see all holes →
              </p>
              <DisplayModeToggle available={handicapsComplete} />
            </div>
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table style={{ borderCollapse: 'separate', borderSpacing: 0, whiteSpace: 'nowrap', fontSize: '0.75rem', minWidth: '100%' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--green-mid)' }}>
                    <th style={{ position: 'sticky', left: 0, background: 'var(--green-deep)', zIndex: 3, padding: '5px 8px', textAlign: 'left', fontFamily: 'var(--font-mono)', fontSize: '0.65rem', color: 'var(--gray-500)', fontWeight: 500, minWidth: 100, borderRight: '1px solid var(--green-mid)' }}>
                      # · Player
                    </th>
                    {allHoles.map(h => (
                      <React.Fragment key={h.hole_number}>
                        <th style={{ padding: '5px 4px', textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: '0.65rem', color: 'var(--gray-500)', fontWeight: 500, width: 28, background: 'var(--green-deep)', borderLeft: h.hole_number === 10 ? '2px solid var(--green-mid)' : 'none' }}>
                          {h.hole_number}
                        </th>
                        {is18 && h.hole_number === 9 && (
                          <th style={{ padding: '5px 5px', textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: '0.65rem', color: 'var(--gray-500)', fontWeight: 500, minWidth: 30, background: 'var(--green-deep)', borderLeft: '2px solid var(--green-mid)' }}>OUT</th>
                        )}
                      </React.Fragment>
                    ))}
                    {is18 && <th style={{ padding: '5px 6px', textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: '0.65rem', color: 'var(--gray-500)', fontWeight: 500, background: 'var(--green-deep)', borderLeft: '2px solid var(--green-mid)', minWidth: 42 }}>IN</th>}
                    {['Tot', '+/–'].map((h, i) => (
                      <th key={h} style={{ padding: '5px 6px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '0.65rem', color: 'var(--gray-500)', fontWeight: 500, background: 'var(--green-deep)', borderLeft: i === 0 ? '2px solid var(--green-mid)' : 'none', minWidth: 42 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {standings.map((sc, idx) => {
                    const isMe = currentPlayer?.id === sc.player_id
                    // Privacy-gated friend row: the server nulled hole_scores
                    // (scorecard sharing is opt-in, default OFF). Never gate the
                    // viewer's OWN row — the server returns it ungated and isMe
                    // is a second guard so a signed-in owner always sees their
                    // own holes. Gross total + vs-par stay visible; only the
                    // per-hole grid is replaced by an explicit empty-state.
                    const isPrivacyGated = !isMe && isScorecardPrivacyGated(sc)
                    // Own row that STILL came back gated — empty hole map WITH a
                    // real gross total — means the server could NOT resolve the
                    // caller from the JWT (expired/revoked session while still
                    // client-signed-in) and withheld even the owner's OWN holes.
                    // Rather than draw a silent all-dashes grid of the user's own
                    // round, surface a distinct session-stale empty-state (never
                    // the "keeps their scorecard private" copy — that's wrong for
                    // your own scores). Gross + vs-par still show.
                    const isOwnScorecardUnavailable = isMe && isScorecardPrivacyGated(sc)
                    const showScorecardEmptyState = isPrivacyGated || isOwnScorecardUnavailable
                    const scorecardEmptyStateMsg = isOwnScorecardUnavailable
                      ? 'Sign in again to see your hole-by-hole scores'
                      : 'This player keeps their scorecard private'
                    const holeScores = typeof sc.hole_scores === 'string' ? JSON.parse(sc.hole_scores) : (sc.hole_scores || {})
                    const holesIn = sc.holes_completed || Object.keys(holeScores).length
                    const rowBg = idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)'
                    const stickyBg = idx % 2 === 0 ? 'var(--green-deep)' : 'var(--green-dark)'

                    const hcpBeforeRow = courseHcpFor(sc)
                    // Per-hole value used in the front/back/total sums. Same
                    // rules as the cell renderer above — best-ball teams get
                    // per-hole MIN of member nets; everyone else gets
                    // gross-minus-personal-strokes.
                    const holeVal = (h) => {
                      if (sc._bestball_members && displayMode === 'adjusted') {
                        const k = String(h.hole_number)
                        const nets = sc._bestball_members
                          .map(m => {
                            const mv = m.hole_scores?.[k]
                            if (mv == null || mv === '') return null
                            const gross = parseInt(mv)
                            if (!Number.isFinite(gross)) return null
                            const mHcp = courseHcpFor(m)
                            const strokes = mHcp ? strokesOnHole(mHcp, h.handicap_rank, allHoles.length) : 0
                            return gross - strokes
                          })
                          .filter(x => x != null)
                        return nets.length ? Math.min(...nets) : 0
                      }
                      const v = holeScores[String(h.hole_number)]
                      if (v == null) return 0
                      const strokes = displayMode === 'adjusted' && hcpBeforeRow
                        ? strokesOnHole(hcpBeforeRow, h.handicap_rank, allHoles.length)
                        : 0
                      return parseInt(v) - strokes
                    }
                    const frontTotal = allHoles.slice(0, 9).reduce((sum, h) => sum + holeVal(h), 0)
                    const backTotal = allHoles.slice(9, 18).reduce((sum, h) => sum + holeVal(h), 0)
                    // gross_total column shows the round total. In adjusted
                    // mode we sum per-hole holeVal() so the number matches
                    // the cells exactly (best-ball teams + individual rows
                    // both fall out of the same accumulator, and the 9-hole
                    // halving is already baked into strokesOnHole).
                    const displayGrossTotal = displayMode === 'adjusted'
                      ? allHoles.reduce((s, h) => s + holeVal(h), 0)
                      : sc.gross_total

                    // +/- vs par. In adjusted mode we re-sum from displayed
                    // per-hole values so the badge matches the cells (the
                    // raw sc.total_score is gross vs par and would leak
                    // through as "the wrong number" in Adjusted).
                    const hasHoleScore = (h) => {
                      const k = String(h.hole_number)
                      if (sc._bestball_members) {
                        return sc._bestball_members.some(m => {
                          const mv = m.hole_scores?.[k]
                          return mv != null && mv !== ''
                        })
                      }
                      return holeScores[k] != null && holeScores[k] !== ''
                    }
                    const dispTotal = displayMode === 'adjusted'
                      ? allHoles.reduce((sum, h) => hasHoleScore(h) ? sum + (holeVal(h) - (h.par || 4)) : sum, 0)
                      : sc.total_score
                    const { txt, cls } = (() => {
                      if (!holesIn && !sc.is_complete) return { txt: '–', cls: '' }
                      const d = dispTotal
                      return d === 0 ? { txt: 'E', cls: 'score-even' }
                        : d < 0 ? { txt: String(d), cls: 'score-under' }
                        : { txt: `+${d}`, cls: 'score-over' }
                    })()

                    return (
                      <tr key={sc.player_id || idx}>
                        <td style={{ position: 'sticky', left: 0, zIndex: 2, background: stickyBg, padding: '6px 8px', borderBottom: '1px solid var(--green-mid)', borderRight: '1px solid var(--green-mid)', borderLeft: isMe ? '3px solid var(--gold)' : 'none', minWidth: 100 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', fontWeight: 500, color: isMe ? 'var(--gold)' : 'var(--gray-500)', minWidth: 16, textAlign: 'right', flexShrink: 0 }}>
                              {sc.is_tied ? '' : sc.finishing_position}
                            </span>
                            <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.75rem', fontWeight: isMe ? 700 : 500, color: isMe ? 'var(--gold)' : 'var(--cream)' }}>
                              {fmtName(sc.player_name)}
                            </span>
                          </div>
                        </td>
                        {showScorecardEmptyState ? (
                          <React.Fragment>
                            <td colSpan={allHoles.length + (is18 ? 2 : 0)} style={{ padding: '6px 12px', textAlign: 'center', borderBottom: '1px solid var(--green-mid)', background: rowBg, color: 'var(--gray-500)', fontSize: '0.72rem', fontStyle: 'italic' }}>
                              {scorecardEmptyStateMsg}
                            </td>
                            <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '0.72rem', fontWeight: 600, color: 'var(--cream)', borderBottom: '1px solid var(--green-mid)', background: rowBg, borderLeft: '2px solid var(--green-mid)' }}>{sc.gross_total || '–'}</td>
                            <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '0.85rem', fontWeight: 600, borderBottom: '1px solid var(--green-mid)', background: rowBg, color: sc.total_score < 0 ? 'var(--blue-birdie)' : sc.total_score > 0 ? 'var(--red)' : 'var(--gray-300)' }}>
                              {sc.total_score == null ? '–' : sc.total_score === 0 ? 'E' : sc.total_score < 0 ? `${sc.total_score}` : `+${sc.total_score}`}
                            </td>
                          </React.Fragment>
                        ) : (<React.Fragment>
                        {allHoles.map(h => {
                          const v = holeScores[String(h.hole_number)]
                          const hcpBefore = courseHcpFor(sc)
                          // Best-ball team row: adjusted mode uses per-hole
                          // MIN of member NETS (each member's gross minus
                          // their own strokes on this hole), which is the
                          // canonical "net best ball" formula. Gross mode
                          // falls back to the raw per-hole team min already
                          // stored on the row.
                          let displayedV
                          if (sc._bestball_members && displayMode === 'adjusted') {
                            const k = String(h.hole_number)
                            const netCandidates = sc._bestball_members
                              .map(m => {
                                const mv = m.hole_scores?.[k]
                                if (mv == null || mv === '') return null
                                const gross = parseInt(mv)
                                if (!Number.isFinite(gross)) return null
                                const mHcp = courseHcpFor(m)
                                const strokes = mHcp ? strokesOnHole(mHcp, h.handicap_rank, allHoles.length) : 0
                                return gross - strokes
                              })
                              .filter(x => x != null)
                            displayedV = netCandidates.length ? Math.min(...netCandidates) : null
                          } else {
                            const strokesHere = displayMode === 'adjusted' && hcpBefore
                              ? strokesOnHole(hcpBefore, h.handicap_rank, allHoles.length)
                              : 0
                            displayedV = v != null
                              ? (displayMode === 'adjusted' ? parseInt(v) - strokesHere : parseInt(v))
                              : null
                          }
                          const cls2 = displayedV != null ? holeClass(displayedV, h.par) : 'empty'
                          // Match-play cell: running match state instead of
                          // stroke count. Only the leading team's row shows
                          // "NUP"; the trailing row (and both on AS) show
                          // empty. Unplayed holes render '–'.
                          let matchCellText = null
                          if (isMatchPlay) {
                            const state = matchStates[h.hole_number]
                            if (!state || !state.played || state.closedOut) {
                              // Holes after the match closes out show '–' even
                              // if scores were recorded — they don't affect the
                              // official result (e.g. 4&3 means the last 3
                              // holes are meaningless).
                              matchCellText = '–'
                            } else {
                              const lead = state.t1Lead
                              const teamNum = sc.team_number || 1
                              if (lead > 0 && teamNum === 1) matchCellText = `${lead}UP`
                              else if (lead < 0 && teamNum === 2) matchCellText = `${-lead}UP`
                              else matchCellText = ''
                            }
                          }
                          return (
                            <React.Fragment key={h.hole_number}>
                              <td style={{ padding: '4px 0', textAlign: 'center', borderBottom: '1px solid var(--green-mid)', background: rowBg, borderLeft: h.hole_number === 10 ? '2px solid var(--green-mid)' : 'none' }}>
                                {isMatchPlay ? (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 22, height: 22, fontSize: '0.7rem', fontFamily: 'var(--font-mono)', fontWeight: 700, color: matchCellText && matchCellText !== '–' ? 'var(--green-bright)' : 'var(--gray-500)' }}>
                                    {matchCellText}
                                  </span>
                                ) : (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, fontSize: '0.72rem', fontFamily: 'var(--font-mono)', fontWeight: 500, ...(holeStyle[cls2] || {}) }}>
                                    {displayedV != null ? displayedV : '–'}
                                  </span>
                                )}
                              </td>
                              {is18 && h.hole_number === 9 && (
                                <td style={{ padding: '4px 4px', textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: '0.72rem', fontWeight: 600, color: 'var(--cream)', borderBottom: '1px solid var(--green-mid)', background: rowBg, borderLeft: '2px solid var(--green-mid)' }}>
                                  {frontTotal || '–'}
                                </td>
                              )}
                            </React.Fragment>
                          )
                        })}
                        {is18 && <td style={{ padding: '4px 6px', textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: '0.72rem', fontWeight: 600, color: 'var(--cream)', borderBottom: '1px solid var(--green-mid)', background: rowBg, borderLeft: '2px solid var(--green-mid)' }}>{backTotal || '–'}</td>}
                        <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '0.72rem', fontWeight: 600, color: 'var(--cream)', borderBottom: '1px solid var(--green-mid)', background: rowBg }}>{displayGrossTotal || '–'}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '0.85rem', fontWeight: 600, borderBottom: '1px solid var(--green-mid)', background: rowBg, color: cls === 'score-under' ? 'var(--blue-birdie)' : cls === 'score-over' ? 'var(--red)' : 'var(--gray-300)' }}>{txt}</td>
                        </React.Fragment>)}
                      </tr>
                    )
                  })}
                  {/* Par row */}
                  <tr>
                    <td style={{ position: 'sticky', left: 0, zIndex: 2, background: '#2a3d2a', padding: '5px 8px', borderBottom: '1px solid var(--green-mid)', borderRight: '1px solid var(--green-mid)' }}>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', fontWeight: 600, color: 'var(--gold)' }}>PAR</span>
                    </td>
                    {allHoles.map(h => (
                      <React.Fragment key={h.hole_number}>
                        <td style={{ padding: '4px 0', textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: '0.72rem', fontWeight: 500, color: 'var(--gray-400)', borderBottom: '1px solid var(--green-mid)', background: 'rgba(210,180,140,0.18)', borderLeft: h.hole_number === 10 ? '2px solid var(--green-mid)' : 'none' }}>{h.par}</td>
                        {is18 && h.hole_number === 9 && (
                          <td style={{ padding: '4px 4px', textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: '0.72rem', fontWeight: 600, color: 'var(--gold)', borderBottom: '1px solid var(--green-mid)', background: 'rgba(210,180,140,0.18)', borderLeft: '2px solid var(--green-mid)' }}>{frontPar}</td>
                        )}
                      </React.Fragment>
                    ))}
                    {is18 && <td style={{ padding: '4px 6px', textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: '0.72rem', fontWeight: 600, color: 'var(--gold)', borderBottom: '1px solid var(--green-mid)', background: 'rgba(210,180,140,0.18)', borderLeft: '2px solid var(--green-mid)' }}>{backPar}</td>}
                    <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '0.72rem', fontWeight: 600, color: 'var(--gold)', borderBottom: '1px solid var(--green-mid)', background: 'rgba(210,180,140,0.18)' }}>{totalPar}</td>
                    <td style={{ borderBottom: '1px solid var(--green-mid)', background: 'rgba(210,180,140,0.18)' }} />
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Per-player detail scorecard — only rendered for team rounds
                where each player records their OWN strokes (best ball,
                match play). Scramble is excluded because all teammates
                share the team's score by design, so the individual rows
                would be identical copies of the team row above and add
                no verification value. */}
            {(roundType === '2v2' || roundType === 'match') && individualScores.length > 0 && (
              <div style={{ marginTop: 24, borderTop: '1px solid var(--green-mid)', paddingTop: 12 }}>
                <h2 style={{ fontSize: '0.85rem', margin: '0 16px 8px', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--gray-300)', fontWeight: 600 }}>
                  Individual Scores
                </h2>
                <p style={{ padding: '0 16px 8px', fontSize: '0.7rem', color: 'var(--gray-500)' }}>
                  Each player's recorded scores, grouped by team. Use these to
                  verify the team result above if a score is in question.
                </p>
                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <table style={{ borderCollapse: 'separate', borderSpacing: 0, whiteSpace: 'nowrap', fontSize: '0.75rem', minWidth: '100%' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--green-mid)' }}>
                        <th style={{ position: 'sticky', left: 0, background: 'var(--green-deep)', zIndex: 3, padding: '5px 8px', textAlign: 'left', fontFamily: 'var(--font-mono)', fontSize: '0.65rem', color: 'var(--gray-500)', fontWeight: 500, minWidth: 120, borderRight: '1px solid var(--green-mid)' }}>
                          Team · Player
                        </th>
                        {allHoles.map(h => (
                          <React.Fragment key={h.hole_number}>
                            <th style={{ padding: '5px 4px', textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: '0.65rem', color: 'var(--gray-500)', fontWeight: 500, width: 28, background: 'var(--green-deep)', borderLeft: h.hole_number === 10 ? '2px solid var(--green-mid)' : 'none' }}>
                              {h.hole_number}
                            </th>
                            {is18 && h.hole_number === 9 && (
                              <th style={{ padding: '5px 5px', textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: '0.65rem', color: 'var(--gray-500)', fontWeight: 500, minWidth: 30, background: 'var(--green-deep)', borderLeft: '2px solid var(--green-mid)' }}>OUT</th>
                            )}
                          </React.Fragment>
                        ))}
                        {is18 && <th style={{ padding: '5px 6px', textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: '0.65rem', color: 'var(--gray-500)', fontWeight: 500, background: 'var(--green-deep)', borderLeft: '2px solid var(--green-mid)', minWidth: 42 }}>IN</th>}
                        <th style={{ padding: '5px 6px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '0.65rem', color: 'var(--gray-500)', fontWeight: 500, background: 'var(--green-deep)', borderLeft: '2px solid var(--green-mid)', minWidth: 42 }}>Tot</th>
                        <th style={{ padding: '5px 6px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '0.65rem', color: 'var(--gray-500)', fontWeight: 500, background: 'var(--green-deep)', minWidth: 42 }}>+/–</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...individualScores]
                        .sort((a, b) => (a.team_number ?? 99) - (b.team_number ?? 99) || (a.player_name || '').localeCompare(b.player_name || ''))
                        .map((sc, idx) => {
                          const isMe = currentPlayer?.id === sc.player_id
                          // Same privacy empty-state as the main table: never
                          // gate the viewer's own row (isMe guard + server keeps
                          // it ungated); show gross + vs-par, hide only the grid.
                          const isPrivacyGated = !isMe && isScorecardPrivacyGated(sc)
                          // Own row still gated => server couldn't resolve the
                          // caller (stale session); show the session-stale copy,
                          // never a blank grid of the user's own scores.
                          const isOwnScorecardUnavailable = isMe && isScorecardPrivacyGated(sc)
                          const showScorecardEmptyState = isPrivacyGated || isOwnScorecardUnavailable
                          const scorecardEmptyStateMsg = isOwnScorecardUnavailable
                            ? 'Sign in again to see your hole-by-hole scores'
                            : 'This player keeps their scorecard private'
                          const holeScores = sc.hole_scores || {}
                          // Individual scorecard mirrors the top scorecard's
                          // display mode. Adjusted subtracts each player's
                          // per-hole strokes (with the 9-hole halving already
                          // handled inside strokesOnHole).
                          const hcpBeforeInd = courseHcpFor(sc)
                          const holeValInd = (h) => {
                            const v = holeScores[String(h.hole_number)]
                            if (v == null) return null
                            const gross = parseInt(v)
                            if (!Number.isFinite(gross)) return null
                            if (displayMode !== 'adjusted' || !hcpBeforeInd) return gross
                            return gross - strokesOnHole(hcpBeforeInd, h.handicap_rank, allHoles.length)
                          }
                          // For the +/- column, sum (score - par) over scored holes.
                          const vsPar = Object.entries(holeScores).reduce((sum, [hn, v]) => {
                            const h = allHoles.find(h => h.hole_number === parseInt(hn))
                            if (!h) return sum
                            const disp = holeValInd(h)
                            return disp == null ? sum : sum + (disp - (h.par || 4))
                          }, 0)
                          const hasAny = Object.keys(holeScores).length > 0
                          const vsTxt = !hasAny ? '–' : vsPar === 0 ? 'E' : vsPar > 0 ? `+${vsPar}` : `${vsPar}`
                          const vsColor = vsPar < 0 ? 'var(--blue-birdie)' : vsPar > 0 ? 'var(--red)' : 'var(--gray-300)'
                          const rowBg = idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)'
                          const stickyBg = idx % 2 === 0 ? 'var(--green-deep)' : 'var(--green-dark)'
                          const frontTotal = allHoles.slice(0, 9).reduce((sum, h) => sum + (holeValInd(h) ?? 0), 0)
                          const backTotal = allHoles.slice(9, 18).reduce((sum, h) => sum + (holeValInd(h) ?? 0), 0)
                          const grossTotal = displayMode === 'adjusted'
                            ? allHoles.reduce((s, h) => s + (holeValInd(h) ?? 0), 0)
                            : (sc.gross_total ?? sc.total_score ?? 0)
                          return (
                            <tr key={sc.player_id || `ind-${idx}`}>
                              <td style={{ position: 'sticky', left: 0, zIndex: 2, background: stickyBg, padding: '6px 8px', borderBottom: '1px solid var(--green-mid)', borderRight: '1px solid var(--green-mid)', borderLeft: isMe ? '3px solid var(--gold)' : 'none', minWidth: 120 }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.62rem', color: 'var(--gold)', fontWeight: 600 }}>
                                    {sc.team_number != null ? `Team ${sc.team_number}` : '—'}
                                  </span>
                                  <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.75rem', fontWeight: isMe ? 700 : 500, color: isMe ? 'var(--gold)' : 'var(--cream)' }}>
                                    {fmtName(sc.player_name)}
                                  </span>
                                </div>
                              </td>
                              {showScorecardEmptyState ? (
                                <React.Fragment>
                                  <td colSpan={allHoles.length + (is18 ? 2 : 0)} style={{ padding: '6px 12px', textAlign: 'center', borderBottom: '1px solid var(--green-mid)', background: rowBg, color: 'var(--gray-500)', fontSize: '0.72rem', fontStyle: 'italic' }}>
                                    {scorecardEmptyStateMsg}
                                  </td>
                                  <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '0.72rem', fontWeight: 600, color: 'var(--cream)', borderBottom: '1px solid var(--green-mid)', background: rowBg, borderLeft: '2px solid var(--green-mid)' }}>{(sc.gross_total ?? sc.total_score) || '–'}</td>
                                  <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '0.85rem', fontWeight: 600, borderBottom: '1px solid var(--green-mid)', background: rowBg, color: sc.total_score < 0 ? 'var(--blue-birdie)' : sc.total_score > 0 ? 'var(--red)' : 'var(--gray-300)' }}>
                                    {sc.total_score == null ? '–' : sc.total_score === 0 ? 'E' : sc.total_score < 0 ? `${sc.total_score}` : `+${sc.total_score}`}
                                  </td>
                                </React.Fragment>
                              ) : (<React.Fragment>
                              {allHoles.map(h => {
                                const dispV = holeValInd(h)
                                const cls2 = dispV != null ? holeClass(dispV, h.par) : 'empty'
                                return (
                                  <React.Fragment key={h.hole_number}>
                                    <td style={{ padding: '4px 0', textAlign: 'center', borderBottom: '1px solid var(--green-mid)', background: rowBg, borderLeft: h.hole_number === 10 ? '2px solid var(--green-mid)' : 'none' }}>
                                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, fontSize: '0.72rem', fontFamily: 'var(--font-mono)', fontWeight: 500, ...(holeStyle[cls2] || {}) }}>
                                        {dispV != null ? dispV : '–'}
                                      </span>
                                    </td>
                                    {is18 && h.hole_number === 9 && (
                                      <td style={{ padding: '4px 4px', textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: '0.72rem', fontWeight: 600, color: 'var(--cream)', borderBottom: '1px solid var(--green-mid)', background: rowBg, borderLeft: '2px solid var(--green-mid)' }}>
                                        {frontTotal || '–'}
                                      </td>
                                    )}
                                  </React.Fragment>
                                )
                              })}
                              {is18 && <td style={{ padding: '4px 6px', textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: '0.72rem', fontWeight: 600, color: 'var(--cream)', borderBottom: '1px solid var(--green-mid)', background: rowBg, borderLeft: '2px solid var(--green-mid)' }}>{backTotal || '–'}</td>}
                              <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '0.72rem', fontWeight: 600, color: 'var(--cream)', borderBottom: '1px solid var(--green-mid)', background: rowBg, borderLeft: is18 ? 'none' : '2px solid var(--green-mid)' }}>{grossTotal || '–'}</td>
                              <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '0.85rem', fontWeight: 600, borderBottom: '1px solid var(--green-mid)', background: rowBg, color: vsColor }}>{vsTxt}</td>
                              </React.Fragment>)}
                            </tr>
                          )
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {sideGames.length > 0 && (
              <div style={{ marginTop: 24, borderTop: '1px solid var(--green-mid)', paddingTop: 12 }}>
                <h2 style={{ fontSize: '0.85rem', margin: '0 16px 8px', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--gray-300)', fontWeight: 600 }}>
                  Side Games — Recap
                </h2>
                <p style={{ padding: '0 16px 12px', fontSize: '0.7rem', color: 'var(--gray-500)' }}>
                  Final results for the games played during this round. TournyFlex tracks the score — players settle any net balance themselves.
                </p>
                <div style={{ padding: '0 16px', display: 'grid', gap: 12 }}>
                  {sideGames.map(g => (
                    <div key={g.id}>
                      {g.format === 'skins' && <SkinsCard game_id={g.id} />}
                      {g.format === 'wolf' && <WolfCard game_id={g.id} readOnly />}
                      {g.format === 'vegas' && <VegasCard game_id={g.id} />}
                      {g.format !== 'skins' && g.format !== 'wolf' && g.format !== 'vegas' && (
                        <div style={{ padding: 10, borderRadius: 8, background: 'var(--green-dark)', border: '1px solid var(--green-mid)', color: 'var(--gray-400)', fontSize: '0.8rem' }}>
                          {g.format} recap coming soon.
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
